import { Container, Graphics, Sprite, type Ticker } from 'pixi.js';
import { AssetLoader } from '../../asset/AssetLoader';
import { AudioBus } from '../../audio/AudioBus';
import { Easings, TweenGroup } from '../../core/Tween';
import { CoinShower } from '../../effects/CoinShower';
import { Fireworks } from '../../effects/Fireworks';
import { WinParticles } from '../../effects/WinParticles';
import type { Theme, WinTier } from '../../theme';
import { CoinAmountLabel } from '../../widgets/CoinAmountLabel';
import { woodGradient } from '../../widgets/board/BoardPanel';
import { COLOURS, displayStyle } from '../../widgets/board/ui';
import type { SpinSettlement } from '../../widgets/slot/settleSpin';
import type { SceneComponent, SceneLayoutContext } from '../SceneComponent';

/**
 * The visual celebration that fires when a spin lands a win: a "+N <coin>" popup
 * that pops in then drifts up, plus a particle burst sized to the win amount.
 *
 * <p>Centred over the slot machine. The {@link burstPosition} getter exposes
 * the centre point so the host scene can use it as the source coordinate for
 * the take-win flying-coin animation — coins appear to fly out of the burst
 * and into the balance label.
 */
export interface WinSequenceOptions {
  /** When provided, every win above the WIN tier swaps in this theme's
   * type-treatment art (BIG WIN / MEGA WIN / SUPER MEGA WIN / JACKPOT)
   * above the +N popup. Themes without {@code assets.scene.winTiers}
   * fall back to the plain text-only popup. */
  theme?: Theme;
}

/**
 * Threshold table for {@link classifyWin} — win amount divided by bet
 * size determines the tier. Tuned loosely to industry-standard slot
 * conventions.
 */
const TIER_THRESHOLDS: ReadonlyArray<{ tier: WinTier; minRatio: number }> = [
  { tier: 'jackpot',        minRatio: 50 },
  { tier: 'super_mega_win', minRatio: 25 },
  { tier: 'mega_win',       minRatio: 10 },
  { tier: 'big_win',        minRatio: 5 },
  { tier: 'win',            minRatio: 1 },
];

/**
 * Classify a win by its amount-to-bet ratio. Returns null for sub-1×
 * wins (no celebration treatment shown — the +N popup still fires
 * via {@link WinSequenceComponent#playWin}).
 */
export function classifyWin(amount: number, bet: number): WinTier | null {
  if (bet <= 0 || amount <= 0) return null;
  const ratio = amount / bet;
  for (const { tier, minRatio } of TIER_THRESHOLDS) {
    if (ratio >= minRatio) return tier;
  }
  return null;
}

export class WinSequenceComponent extends Container implements SceneComponent {
  private readonly popup: CoinAmountLabel;
  /** Dark plinth that sits BEHIND the {@link popup} so the +N digits
   *  read against a guaranteed dark background regardless of which
   *  tier-treatment art (gold JACKPOT, gold MEGA, etc.) is showing.
   *  Sized per-popup-redraw so it always hugs the digit string. */
  private readonly popupPlinth: Graphics;
  private readonly particles: WinParticles;
  /** Gold-coin rain for JACKPOT tier only. Drawn ABOVE the win
   *  treatment + popup so the coins fall in front of the headline art
   *  for full theatre. Producer brief 2026-06-04. */
  private readonly coinShower: CoinShower;
  /** Multi-rocket fireworks volley — escalates by tier. Producer
   *  brief 2026-06-04 "Fireworks?" — big wins should feel like an
   *  event. Mega+ tiers each fire a sized volley over the slot area.
   *  Drawn BEHIND the headline art (added before the tier sprite in
   *  the child list) so the fireworks bloom around the BIG WIN /
   *  MEGA / JACKPOT lettering rather than obscuring it. */
  private readonly fireworks: Fireworks;
  private readonly tweens = new TweenGroup();
  private readonly tierTreatment: Sprite;
  private popupBaseY = 0;
  private readonly theme: Theme | null;
  /** Cached viewport bounds from the most recent layout — used by the
   *  jackpot coin shower to know where to spawn / clean up. */
  private viewportWidth = 0;
  private viewportHeight = 0;

  constructor(ticker: Ticker, options: WinSequenceOptions = {}) {
    super();

    this.theme = options.theme ?? null;

    this.particles = new WinParticles(ticker);
    this.addChild(this.particles);

    // Fireworks — added BEFORE the coin shower + tier treatment so
    // the rocket trails + bursts bloom BEHIND the headline art
    // (Pixi child order = z-order). Mega-tier and above each fire a
    // sized volley; smaller tiers don't trigger it.
    this.fireworks = new Fireworks(ticker);
    this.addChild(this.fireworks);

    // Coin shower for jackpot tier — added BEFORE the tier-treatment
    // sprite so the coins fall BEHIND the headline art (sprite ordering
    // = z-order in Pixi). The shower itself is a Container so adding
    // it once at ctor is fine; the per-jackpot {@code coinShower.rain}
    // call below schedules emission.
    this.coinShower = new CoinShower(ticker);
    this.addChild(this.coinShower);

    // Tier-treatment sprite — swapped in / out via setTexture as wins
    // are classified. Anchored centre, hidden by default. Sits ABOVE
    // the +N popup (the popup is a coin counter; the treatment is the
    // headline "BIG WIN" art).
    this.tierTreatment = new Sprite();
    this.tierTreatment.anchor.set(0.5);
    this.tierTreatment.visible = false;
    this.tierTreatment.alpha = 0;
    this.addChild(this.tierTreatment);

    // Dark plinth behind the popup — drawn each redraw to hug the
    // current label width. Sits beneath the popup in the child list.
    this.popupPlinth = new Graphics();
    this.popupPlinth.alpha = 0;
    this.addChild(this.popupPlinth);

    // Popup label. Producer audit Phase 4 (round 2): theme palettes
    // were overriding the defaults with low-contrast colours (forest's
    // accent is green; warm is gold → green stroke on gold digits on
    // gold tier-treatment = unreadable). Defaults are now hard-coded
    // to white digits with a heavy dark navy stroke + drop shadow,
    // backed by a dark plinth that sits behind the digits. Caller
    // overrides are still respected but the scene no longer passes
    // theme colours through (see SlotDemoScene).
    // The win in the kit's display type, gold with a dark outline, on a
    // carved wooden plate (producer 2026-10-14: no pills, no purple).
    this.popup = new CoinAmountLabel('', {
      style: { ...displayStyle(36, COLOURS.gold), align: 'center' },
    });
    this.popup.alpha = 0;
    this.addChild(this.popup);
  }

  layout(ctx: SceneLayoutContext): void {
    const cx = ctx.centerX;
    const cy = Math.round((ctx.machineTop + ctx.machineBottom) / 2);
    this.viewportWidth = ctx.width;
    this.viewportHeight = ctx.height;
    this.particles.x = cx;
    this.particles.y = cy;
    // Coin shower + fireworks both live in scene-local coords (no
    // offset) so their spawn areas can address the full viewport in
    // absolute coordinates via their respective option objects.
    this.coinShower.x = 0;
    this.coinShower.y = 0;
    this.fireworks.x = 0;
    this.fireworks.y = 0;
    this.popup.x = cx;
    // Producer audit Phase 4 (round 4) — vertical layout reworked so
    // the tier-treatment headline (BIG WIN / JACKPOT etc.) sits cleanly
    // ABOVE the +N popup with no overlap. Previously popup was at
    // cy+60 and treatment at cy-20 → with the new dark plinth behind
    // the popup, plinth's top edge overlapped the treatment's bottom.
    //
    // New offsets:
    //   tier treatment center y = cy - 70  (was cy - 20)
    //   popup center y          = cy + 110 (was cy + 60)
    // Gap between treatment bottom (~cy-10 for a 120px-tall sprite)
    // and popup plinth top (~cy+68) = ~78px — comfortable read.
    //
    // 2026-06-09 — on a short (mobile) machine, cy+110 landed BEHIND the
    // SPIN disc (which lifts up into the frame's bottom edge), leaving the
    // win amount illegible. Clamp the popup to sit clear of the disc's top
    // edge. Disc geometry mirrors SlotHudComponent: a 192px disc (96 half)
    // lifted 32px, both at the same hudScale. Tall (desktop) machines
    // already clear it, so the Math.min is a no-op there.
    const hudScale = Math.max(0.55, Math.min(1.0, ctx.width / 2000));
    const spinDiscTopY = ctx.spinButtonY - 128 * hudScale; // 96 half + 32 lift
    const POPUP_CLEARANCE = 56; // popup half-height + plinth pad + breathing gap
    this.popup.y = Math.min(cy + 110, spinDiscTopY - POPUP_CLEARANCE);
    this.popupBaseY = this.popup.y;
    this.tierTreatment.x = cx;
    // Anchor the tier headline a fixed 180px above the +N popup (was a
    // flat cy-70). Desktop spacing is identical — popup at cy+110 →
    // treatment at cy-70 — but on mobile, where the popup is clamped UP
    // off the SPIN disc, the headline now rides up with it instead of
    // stranding low over the reels (producer 2026-06-10).
    this.tierTreatment.y = this.popup.y - 180;
    // Cap treatment width to ~52% of the viewport so JACKPOT doesn't
    // overflow on narrow phones (1.5× the popup font size, generous on
    // tablet). Aspect-preserving.
    const targetWidth = Math.min(420, ctx.width * 0.52);
    const sprite = this.tierTreatment;
    if (sprite.texture && sprite.texture.width > 0 && targetWidth > 0) {
      const scale = targetWidth / sprite.texture.width;
      sprite.scale.set(scale);
    }
  }

  destroy(): void {
    this.tweens.cancelAll();
    this.particles.destroyEffect();
    this.coinShower.destroyEffect();
    this.fireworks.destroyEffect();
    super.destroy({ children: true });
  }

  /** Stage-local centre of the win burst — used as the source coordinate for
   * the take-win flying-coin animation. */
  get burstPosition(): { x: number; y: number } {
    return { x: this.particles.x, y: this.particles.y };
  }

  /** Fire the burst + popup pair sized to the settlement total. When
   * the win clears the BIG-WIN threshold, also swaps in the matching
   * tier-treatment art and animates it through a longer, more
   * theatrical sequence — pulse animation during a held celebration
   * phase, plus continuous confetti for mega-win-or-larger.
   *
   * <p>Producer audit Phase 4: previously the tier treatment popped
   * in for 350ms then immediately faded over 1.2s (~1.55s total) —
   * playtest feedback was that big wins felt rushed. The new pacing:
   *
   * <ul>
   *   <li>BIG WIN    — 350ms pop-in → 1500ms pulse hold → 700ms fade
   *   <li>MEGA WIN   — 350ms pop-in → 2200ms pulse hold + confetti → 700ms fade
   *   <li>SUPER MEGA — 350ms pop-in → 3000ms pulse hold + confetti → 700ms fade
   *   <li>JACKPOT    — 350ms pop-in → 4000ms pulse hold + confetti → 700ms fade
   * </ul>
   *
   * <p>Pulse oscillates scale ±5% at a 600ms cycle so the headline
   * "breathes" through the hold. Confetti is a continuous trickle of
   * slower-falling, longer-living particles in a wider colour palette
   * — emitted every {@link CONFETTI_INTERVAL_MS} during the hold for
   * the mega-and-above tiers.
   */
  playWin(settlement: SpinSettlement, betSize?: number): void {
    // ---- Win-tier classification (drives audio, treatment, hold + confetti). ----
    const tier = betSize
      ? classifyWin(settlement.totalWinnings, betSize)
      : null;

    // ---- Initial burst — count + intensity scale with tier. ----
    const baseBurstCount = Math.min(160, 30 + Math.floor(settlement.totalWinnings * 0.6));
    const tierBurstBoost = (
      tier === 'jackpot' ? 2.0
      : tier === 'super_mega_win' ? 1.6
      : tier === 'mega_win' ? 1.4
      : tier === 'big_win' ? 1.2
      : 1.0
    );
    const burstCount = Math.floor(baseBurstCount * tierBurstBoost);
    this.particles.burst(burstCount, {
      tints: [0xff6bd6, 0xfff59d, 0x42e2f5, 0xffd23b, 0xff8a3d],
      lifeMs: 1200,
      minSpeed: 220,
      maxSpeed: 540,
      gravity: 700,
      radius: 5,
    });

    // ---- +N popup label setup ----
    this.popup.setText(`+${settlement.totalWinnings.toLocaleString()}`);
    this.popup.alpha = 0;
    this.popup.scale.set(0.6);
    // Reset y in case a previous fade-out drifted it. {@link layout} keeps
    // popupBaseY current.
    this.popup.y = this.popupBaseY;

    // Redraw the dark plinth to wrap the popup. We can't read the
    // popup's bounds reliably before the first render, so we estimate
    // from the digit string length + a generous side padding. The
    // plinth's alpha is bound to the popup's via the same tween.
    this.redrawPopupPlinth(settlement.totalWinnings.toLocaleString());
    this.popupPlinth.alpha = 0;
    this.popupPlinth.scale.set(0.6);
    this.popupPlinth.x = this.popup.x;
    this.popupPlinth.y = this.popup.y;

    // ---- Audio cue per tier ----
    // Producer brief 2026-06-04 — wire each tier to its dedicated
    // sting now that all four are commissioned. Previous mapping
    // collapsed jackpot+super_mega onto sting_big and mega+big onto
    // sting_medium, which meant the jackpot moment didn't sound
    // distinct from a super_mega even though the celebration ran
    // longer + raised the burst count. Now:
    //   jackpot          → sting_jackpot  (+ coin shower, see below)
    //   super_mega_win   → sting_big
    //   mega_win / big   → sting_medium
    //   win              → sting_small
    if (tier === 'jackpot') {
      AudioBus.shared.play('win:sting_jackpot', { volume: 1.0 });
    } else if (tier === 'super_mega_win') {
      AudioBus.shared.play('win:sting_big');
    } else if (tier === 'mega_win' || tier === 'big_win') {
      AudioBus.shared.play('win:sting_medium');
    } else if (tier === 'win') {
      AudioBus.shared.play('win:sting_small');
    }

    // ---- Mega+ tiers: fireworks volley. ----
    // Rocket count + spread scale with tier so the display escalates
    // in scope, not just intensity. mega = a small flurry; jackpot =
    // a full finale that runs the length of the celebration hold.
    // Smaller tiers (win, big_win) skip fireworks entirely — they're
    // still loud enough at the bottom of the ladder that more visual
    // would dilute the bigger payouts above.
    if (
      this.viewportWidth > 0
      && (tier === 'mega_win' || tier === 'super_mega_win' || tier === 'jackpot')
    ) {
      const volleyCount = (
        tier === 'jackpot' ? 9
        : tier === 'super_mega_win' ? 6
        : 4 // mega_win
      );
      const volleySpreadMs = (
        tier === 'jackpot' ? 3200
        : tier === 'super_mega_win' ? 2400
        : 1800 // mega_win
      );
      this.fireworks.show({
        count: volleyCount,
        spreadMs: volleySpreadMs,
        // Spawn band runs the full width; apex band is the upper half
        // of the viewport so rockets always burst above the machine
        // centre, never below the bottom toolbar.
        areaLeft: this.viewportWidth * 0.05,
        areaRight: this.viewportWidth * 0.95,
        groundY: this.viewportHeight + 20,
        apexTopY: this.viewportHeight * 0.08,
        apexBottomY: this.viewportHeight * 0.45,
        // Jackpot gets brighter trails + bigger bursts on top of the
        // longer volley window.
        burstCount: tier === 'jackpot' ? 48 : 36,
        launchMs: tier === 'jackpot' ? 820 : 720,
      });
    }

    // ---- Jackpot-only: coin shower from the top of the screen. ----
    // Spawns ~80 coins over ~3 seconds spanning the full viewport
    // width, landing past the bottom edge. Visually distinct from the
    // confetti emission (smaller, faster, painted sprites vs round
    // particles) so the two reinforce each other rather than overlap.
    if (tier === 'jackpot' && this.viewportWidth > 0) {
      this.coinShower.rain({
        areaLeft: 0,
        areaRight: this.viewportWidth,
        topY: -40,
        cleanupY: this.viewportHeight + 60,
        totalCoins: 80,
        durationMs: 3000,
        coinSize: 42,
        minSpeed: 360,
        maxSpeed: 640,
        gravity: 1100,
        spinRate: 5,
      });
    }

    // ---- Tier-treatment headline art (BIG WIN / MEGA WIN / ...) ----
    const tierEntry = tier && this.theme?.assets?.scene?.winTiers?.[tier];
    const tierTexture = tierEntry
      ? AssetLoader.shared.getTexture(tierEntry.alias)
      : null;
    // 'win' tier skips the headline treatment — only big_win and above
    // get the long celebration. The popup still fires for plain wins.
    const showTreatment =
      tier !== null && tier !== 'win' && tierTexture !== null;
    const showConfetti =
      tier === 'mega_win'
      || tier === 'super_mega_win'
      || tier === 'jackpot';
    const holdMs = (
      tier === 'jackpot' ? 4000
      : tier === 'super_mega_win' ? 3000
      : tier === 'mega_win' ? 2200
      : tier === 'big_win' ? 1500
      : 0
    );

    if (showTreatment) {
      this.tierTreatment.texture = tierTexture;
      this.tierTreatment.visible = true;
      this.tierTreatment.alpha = 0;
      // Re-fit width now that we have a real texture.
      const targetWidth = Math.min(420, this.parent ? 600 : 420);
      const baseScale = this.tierTreatment.texture.width > 0
        ? targetWidth / this.tierTreatment.texture.width
        : 1;
      this.tierTreatment.scale.set(baseScale * 0.6); // start at 60% for pop-in

      // Phase 1 — pop in (350ms, easeOutBack).
      this.tweens.start({
        durationMs: 350,
        easing: Easings.easeOutBack,
        onUpdate: (p) => {
          this.tierTreatment.alpha = p;
          this.tierTreatment.scale.set(baseScale * (0.6 + p * 0.4));
        },
        onComplete: () => {
          this.runHoldPhase(baseScale, holdMs, showConfetti);
        },
      });
    } else {
      this.tierTreatment.visible = false;
    }

    // ---- +N popup animation ----
    // The popup pop-in is short; the popup also follows the tier
    // treatment through a held phase so the +N stays visible during
    // the celebration (used to fade after 900ms; now stays until the
    // tier treatment is also fading).
    const popupHoldMs = holdMs > 0 ? holdMs : 600;
    this.tweens.start({
      durationMs: 220,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.popup.alpha = p;
        this.popup.scale.set(0.6 + p * 0.6);
        this.popupPlinth.alpha = p;
        this.popupPlinth.scale.set(0.6 + p * 0.6);
      },
      onComplete: () => {
        // Idle hold for the popup — sits at full alpha while the tier
        // treatment runs its pulse phase. Then fades.
        this.tweens.start({
          durationMs: popupHoldMs,
          easing: Easings.linear,
          onUpdate: () => {
            // Static — alpha stays at 1; scale already locked at peak.
          },
          onComplete: () => {
            this.tweens.start({
              durationMs: 700,
              easing: Easings.easeOutCubic,
              onUpdate: (p) => {
                this.popup.alpha = 1 - p;
                this.popup.y -= 0.5;
                this.popupPlinth.alpha = 1 - p;
                this.popupPlinth.y -= 0.5;
              },
            });
          },
        });
      },
    });
  }

  /** Draw the dark plinth backing for the popup. Sized from the
   *  digits string so a short "+100" gets a tight plinth and a long
   *  "+1,000,000" gets a wide one. The plinth is anchored at (0,0)
   *  in its own coord space; the parent positions it to overlap the
   *  popup's centre. */
  private redrawPopupPlinth(digitsText: string): void {
    // Approximate width — each digit / character at 36px Fredoka 900
    // is roughly 22-26px wide; "+" is narrower. We over-estimate
    // slightly and pad heavily so the plinth never clips the digits.
    const approxCharWidth = 24;
    const labelWidth = (digitsText.length + 1) * approxCharWidth; // +1 for the "+"
    const coinIconWidth = 36; // matches the popup's coin sprite at fontSize 36
    const totalWidth = labelWidth + 6 + coinIconWidth; // 6px gap to coin
    const paddingX = 24;
    const paddingY = 12;
    const w = totalWidth + paddingX * 2;
    const h = 36 + paddingY * 2; // fontSize + padding
    this.popupPlinth.clear();
    this.popupPlinth
      .roundRect(-w / 2 + 2, -h / 2 + 4, w, h, 10)
      .fill({ color: 0x000000, alpha: 0.45 })
      .roundRect(-w / 2, -h / 2, w, h, 10)
      .fill(woodGradient())
      .stroke({ color: COLOURS.brass, width: 2 })
      .roundRect(-w / 2 + 3, -h / 2 + 3, w - 6, h - 6, 8)
      .stroke({ color: COLOURS.brassLight, alpha: 0.25, width: 1 });
  }

  /** Hold phase for the tier-treatment headline — runs after pop-in
   *  and before fade-out. Drives a subtle scale-pulse on the treatment
   *  art, and (for the biggest tiers) emits a stream of confetti
   *  particles to fill the screen behind the headline.
   *
   *  <p>Confetti emission uses a counter to schedule periodic bursts
   *  inside a single linear-progress tween, so we don't fight the
   *  TweenGroup's cancel-on-destroy lifecycle. */
  private runHoldPhase(baseScale: number, holdMs: number, confetti: boolean): void {
    if (holdMs <= 0) {
      // Tier was too small for a hold — fade out immediately.
      this.fadeTreatmentOut(700);
      return;
    }
    let confettiBurstsFired = 0;
    this.tweens.start({
      durationMs: holdMs,
      easing: Easings.linear,
      onUpdate: (p) => {
        // Pulse — scale oscillates ±5% around the base scale at a
        // ~600ms cycle. sin(2π * cycles) where cycles = elapsed / 600ms.
        const elapsedMs = p * holdMs;
        const cycles = elapsedMs / 600;
        const pulse = Math.sin(cycles * Math.PI * 2);
        this.tierTreatment.scale.set(baseScale * (1 + pulse * 0.05));

        // Confetti emission — schedule a burst every CONFETTI_INTERVAL_MS.
        if (confetti) {
          const dueBursts = Math.floor(elapsedMs / CONFETTI_INTERVAL_MS);
          while (confettiBurstsFired < dueBursts) {
            this.emitConfettiBurst();
            confettiBurstsFired += 1;
          }
        }
      },
      onComplete: () => {
        // Reset to base scale before fading so the fade-out doesn't
        // catch mid-pulse and snap.
        this.tierTreatment.scale.set(baseScale);
        this.fadeTreatmentOut(700);
      },
    });
  }

  /** Single confetti burst — wider colour palette, longer life,
   *  gentler gravity than the headline burst so the particles drift
   *  like real confetti rather than exploding outward. */
  private emitConfettiBurst(): void {
    this.particles.burst(22, {
      tints: [
        0xff6bd6, 0xfff59d, 0x42e2f5, 0xffd23b, 0xff8a3d,
        0xb475ff, 0x9de54e, 0xff5a8a,
      ],
      lifeMs: 2000,
      minSpeed: 80,
      maxSpeed: 220,
      gravity: 280,
      radius: 4,
    });
  }

  /** Fade the tier-treatment headline out over the given duration,
   *  then hide it. Called both after the hold phase and (for sub-
   *  big-win tiers) directly from {@link playWin} when no hold
   *  applies. */
  private fadeTreatmentOut(durationMs: number): void {
    this.tweens.start({
      durationMs,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.tierTreatment.alpha = 1 - p;
      },
      onComplete: () => {
        this.tierTreatment.visible = false;
      },
    });
  }
}

/** Confetti cadence — a burst every N ms during the hold phase of
 *  mega-win-or-larger celebrations. Tuned so the screen feels alive
 *  with falling colour without overwhelming the headline art. */
const CONFETTI_INTERVAL_MS = 240;
